import { useTranslator } from "@eo-locale/react";
import { Fragment, useState } from "react";
import Cookies from "js-cookie";
import isEmpty from "lodash/isEmpty";

import { useAppSelector } from "@/redux/store/hooks";
import {
  Text,
  Button,
  Heading,
  HTMLWrapper,
  ButtonGroup,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Divider,
  Switch,
  FormControl,
  FormLabel,
  Stack,
  VisuallyHidden
} from "@/storybook-components";

interface CookieConsentProps {
  zIndex?: number;
}

const CookieConsent = (props: CookieConsentProps): JSX.Element => {
  const { translate } = useTranslator();
  const { zIndex = 1002 } = props;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    cookiePolicy,
    disableFacebookSdk,
    disableTwitterWidget,
    disableGoogleAnalytics
  } = useAppSelector((state) => state.features.features);
  const { settings } = useAppSelector((state) => state.settings);

  const isCookiePolicyEnabled =
    cookiePolicy?.enabled && isEmpty(Cookies.get("cookie_consent"));

  const isSocialAnalyticsEnabled =
    !disableFacebookSdk?.enabled || !disableTwitterWidget?.enabled;
  const isUserTrackingAnalyticsEnabled =
    !disableGoogleAnalytics?.enabled ||
    (settings?.trafficTrackingEnabled && !!settings?.googleAnalyticsAccount);

  const [isAnalyticsCookieEnabled, setAnalyticsCookie] = useState(
    isUserTrackingAnalyticsEnabled
  );
  const [isSocialCookieEnabled, setSocialCookie] = useState(
    isSocialAnalyticsEnabled
  );
  const [showCookieConsent, setShowCookieConsent] = useState(
    isCookiePolicyEnabled
  );

  const cookiesList = [
    { name: "essential", status: true, readOnly: true },
    {
      name: "analytics",
      readOnly: false,
      status: isUserTrackingAnalyticsEnabled
    },
    {
      name: "socialMedia",
      readOnly: false,
      status: isSocialAnalyticsEnabled
    }
  ];

  const setCookieConsent = () => {
    const currentDate = new Date();

    // Creating a expiry date for the cookie which is exactly after two years
    currentDate.setMonth(currentDate.getMonth() + 24);

    const cookieObject = {
      functional: true,
      analytics: isAnalyticsCookieEnabled,
      social: isSocialCookieEnabled
    };

    Cookies.set("cookie_consent", JSON.stringify(cookieObject), {
      expires: currentDate
    });

    /* global gtag */
    if (
      isAnalyticsCookieEnabled &&
      (process.env.GA_MEASUREMENT_ID || settings?.googleAnalyticsAccount) &&
      process.env.NODE_ENV === "production"
    ) {
      // @ts-ignore
      gtag("consent", "update", { analytics_storage: "granted" });
    }

    setShowCookieConsent(false);
  };

  const handleChange = (cookieName: string, value: boolean): void => {
    if (cookieName === "analytics") {
      setAnalyticsCookie(value);
    } else {
      setSocialCookie(value);
    }
  };

  if (!showCookieConsent) return null;

  return (
    <Stack
      px={4}
      py={6}
      w="100%"
      bg="white"
      pos="fixed"
      bottom={0}
      zIndex={zIndex}
      boxShadow="xs"
    >
      <Heading as="h2" size="md">
        {translate("common.cookieConsent.title")}
      </Heading>
      <Text>
        <HTMLWrapper html={translate("common.cookieConsent.description")} />
      </Text>
      <ButtonGroup>
        <Button variant="primary" onClick={setCookieConsent}>
          {translate("common.cookieConsent.iamOkWithThat")}
        </Button>
        <Button variant="secondary" onClick={onOpen}>
          {translate("common.cookieConsent.manageCookies")}
        </Button>
      </ButtonGroup>

      <Modal
        size="xl"
        isOpen={isOpen}
        onClose={onClose}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading as="h2" size="md">
              {translate("common.cookieConsent.manageCookies")}
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              <HTMLWrapper
                html={translate("common.cookieConsent.aboutCookies")}
              />
            </Text>

            <Divider my={2} />

            {cookiesList.map(({ name, status, readOnly }) => {
              return (
                <Fragment key={name}>
                  <Text fontSize="lg" fontWeight="bold">
                    {translate(`common.cookieConsent.${name}Cookies`)}
                  </Text>
                  <FormControl
                    display="flex"
                    alignItems="center"
                    isDisabled={!status}
                  >
                    <FormLabel
                      mb={0}
                      fontWeight="normal"
                      htmlFor={`${name}Cookies`}
                    >
                      {translate(
                        `common.cookieConsent.${name}CookiesDescription`
                      )}
                    </FormLabel>
                    <VisuallyHidden>
                      {translate(`common.cookieConsent.${name}Cookies`)}
                    </VisuallyHidden>
                    <Switch
                      defaultChecked={status}
                      id={`${name}Cookies`}
                      isDisabled={!status}
                      isReadOnly={readOnly}
                      onChange={(e) => handleChange(name, e.target.checked)}
                    />
                  </FormControl>

                  <Divider my={2} />
                </Fragment>
              );
            })}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="primary"
                onClick={() => {
                  setCookieConsent();
                  onClose();
                }}
              >
                {translate("common.saveAndClose")}
              </Button>
              <Button variant="secondary" onClick={onClose}>
                {translate("common.cancel")}
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

export default CookieConsent;
